import React from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiAlertTriangle, FiHeart, FiBriefcase, FiUsers, FiTrendingDown } = FiIcons;

const AgitationSection = () => {
  const problems = [
    {
      icon: FiHeart,
      title: "เรื่องรัก",
      description: "ทุ่มเทแทบตาย สุดท้ายเขาไปเลือกคนอื่น? หรือแต่งงานกันมา 10 ปีเพิ่งมารู้ว่า \"เข้ากันไม่ได้\" ในวันที่สายไป?",
      color: "from-red-500 to-pink-500"
    },
    {
      icon: FiBriefcase,
      title: "เรื่องงาน",
      description: "หัวหน้าไม่เอ็นดู ลูกน้องนินทาลับหลัง หรือเพื่อนร่วมงานที่ต่อหน้ายิ้มหวาน แต่ลับหลังพร้อมแทงคุณทุกเมื่อ?",
      color: "from-blue-500 to-indigo-500"
    },
    {
      icon: FiUsers,
      title: "เรื่องธุรกิจ",
      description: "หุ้นส่วนที่ดูน่าเชื่อถือ พูดจาดีโปรไฟล์หรู... แต่วันดีคืนดีกลับหักหลังคุณจนหมดตัว?",
      color: "from-green-500 to-emerald-500"
    }
  ];

  return (
    <section className="py-20 px-4 relative">
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-red-900/10 to-transparent"></div>
      
      <div className="relative z-10 max-w-6xl mx-auto">
        <motion.div
          initial={{ y: 30, opacity: 0 }}
          whileInView={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            ถามจริงๆ ครับ... คุณ<span className="text-red-400">เหนื่อย</span>ไหมกับการ 
            <span className="bg-gradient-to-r from-red-400 to-pink-400 bg-clip-text text-transparent"> "เดาใจคน"</span>?
          </h2>
          
          <div className="max-w-4xl mx-auto text-lg md:text-xl text-gray-300 space-y-4">
            <p>
              ในโลกการทำงานและชีวิตจริง... ความเก่งเรื่องงาน (IQ) พาคุณไปได้ไกลแค่ครึ่งทาง
            </p>
            <p>
              แต่อีกครึ่งทางที่เหลือ... ขึ้นอยู่กับว่า <span className="text-pink-400 font-semibold">"คุณดีลกับคนเป็นไหม?"</span>
            </p>
          </div>
        </motion.div>

        <motion.div
          initial={{ y: 30, opacity: 0 }}
          whileInView={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          viewport={{ once: true }}
          className="mb-12"
        >
          <h3 className="text-2xl md:text-3xl font-semibold text-white mb-8 text-center">
            ลองเช็คดูว่า คุณกำลังเจอ <span className="text-red-400">"Bug"</span> ในความสัมพันธ์เหล่านี้อยู่หรือเปล่า:
          </h3>
        </motion.div>

        <div className="grid md:grid-cols-3 gap-8 mb-16">
          {problems.map((problem, index) => (
            <motion.div
              key={index}
              initial={{ y: 50, opacity: 0 }}
              whileInView={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.8, delay: index * 0.2 }}
              viewport={{ once: true }}
              className="group"
            >
              <div className="bg-slate-800/50 backdrop-blur-sm border border-slate-700/50 rounded-2xl p-6 h-full hover:border-red-500/50 transition-all duration-300 hover:shadow-2xl hover:shadow-red-500/10">
                <div className={`w-16 h-16 bg-gradient-to-br ${problem.color} rounded-full flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300`}>
                  <SafeIcon icon={problem.icon} className="text-2xl text-white" />
                </div>
                
                <h4 className="text-xl font-bold text-white mb-4 flex items-center">
                  💔 {problem.title}
                </h4>
                
                <p className="text-gray-300 leading-relaxed">
                  {problem.description}
                </p>
              </div>
            </motion.div>
          ))}
        </div>

        <motion.div
          initial={{ y: 30, opacity: 0 }}
          whileInView={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.6 }}
          viewport={{ once: true }}
          className="text-center bg-slate-800/30 backdrop-blur-sm border border-red-500/30 rounded-2xl p-8"
        >
          <div className="flex items-center justify-center mb-6">
            <SafeIcon icon={FiAlertTriangle} className="text-4xl text-red-400 mr-4" />
            <h3 className="text-2xl md:text-3xl font-bold text-white">
              ปัญหาไม่ใช่คุณไม่ดี... แต่คุณ <span className="text-red-400">"อ่านเขาไม่ออก"</span>
            </h3>
          </div>
          
          <div className="max-w-4xl mx-auto text-lg text-gray-300 space-y-4">
            <p>
              คุณกำลังใช้แค่ <span className="text-yellow-400">"สัญชาตญาณ"</span> หรือแบบทดสอบจิตวิทยาพื้นๆ อย่าง MBTI ที่ใครๆ ก็<span className="text-red-400">"เฟค"</span> คำตอบได้
            </p>
            <p className="text-xl font-semibold text-pink-400">
              แต่คุณลืมไปว่า... คนเราโกหกคำพูดได้ แต่โกหก <span className="text-white">"วันเกิด"</span> และ <span className="text-white">"ธาตุแท้"</span> ไม่ได้
            </p>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default AgitationSection;